import { formatMoney } from '../../utils';
import { formatLossDate } from './lossDates';

/* Maps a correction's stored field name onto what a human reads. The stored names are the overlay's
   column names, which is why they are snake_case and why an unknown one falls through to itself
   rather than vanishing — a field added server-side should still be legible here. */
const LABELS = {
  loss_cause: 'Loss Cause',
  description: 'Description',
  incurred: 'Indemnity',
  expense: 'Expense',
  recovery: 'Recovery',
  location_id: 'Location',
  occurred_at: 'Occurred',
};

const MONEY_FIELDS = ['incurred', 'expense', 'recovery'];

const RESTORING = 'Restoring';
const REMOVING = 'Removing';
const REVERTING = 'Reverting';
const REMOVED = 'Removed';
const REVIEW_FEED = 'Review feed';
const CORRECTED = 'Corrected';
const NEEDS_INDEMNITY = 'Needs indemnity';

// The two states a click can actually explain — the removal/restore staging states and the
// persisted exclusion have nothing for the modal to show.
export const CLICKABLE_FLAGS = [CORRECTED, REVIEW_FEED];

/* Single source for the grid's Flags cell text: precedence is a staged restore first (it describes
   what Save is about to undo, outranking everything else about the row, including the staged removal
   it's mutually exclusive with), then a staged removal (what Save is about to do next), then a
   staged revert (same logic — it says what Save is about to erase), then a persisted exclusion
   (already gone, but distinct from "about to go"), then a feed divergence (still needs a human
   even though it's "corrected"), then a plain correction. Shared by the Flags column and the diff
   dialog's heading so the modal can never contradict the cell the user clicked. */
export const lossFlag = (row) => {
  if (!row) return '';
  if (row._restored) return RESTORING;
  if (row._removed) return REMOVING;
  if (row._reverted) return REVERTING;
  // A self-reported loss whose indemnity UND-1050 could not determine. It stays out of every loss
  // pick until someone enters it.
  if (row.source === 'SELF_REPORTED' && (row.incurred == null || row.incurred === '')) return NEEDS_INDEMNITY;
  if (row.excluded) return REMOVED;
  if (Array.isArray(row.divergedFields) && row.divergedFields.length) return REVIEW_FEED;
  return row.corrected ? CORRECTED : '';
};

/* The most recent editedAt across a claim line's corrections. editedAt may arrive as a numeric
   string, hence the Number() coercion for comparison (the returned value is left as-is). Null when
   nothing has an editedAt, including an empty list. */
export const newestEditedAt = (entries) =>
  (entries || []).reduce((latest, entry) => (entry.editedAt && (!latest || Number(entry.editedAt) > Number(latest)) ? entry.editedAt : latest), null);

export const correctionFieldLabel = (field) => (field ? LABELS[field] || field : '');

export const formatCorrectionValue = (field, value) => {
  if (value == null || value === '') {
    // A blank loss cause is the Unclassified state the grid already names; anywhere else a blank is
    // a value the human deliberately cleared, and an empty cell would read as "no data".
    return field === 'loss_cause' ? 'Unclassified' : '(cleared)';
  }
  if (MONEY_FIELDS.includes(field)) {
    const amount = Number(value);
    // Guard against non-numeric values before passing to formatMoney, which would return "$ NaN".
    return Number.isNaN(amount) ? String(value) : formatMoney(amount, { prefix: '$' });
  }
  if (field === 'occurred_at') {
    return formatLossDate(value);
  }
  return String(value);
};

/* A blank feed value is never a human's deliberate clearing — the feed doesn't clear fields, it
   either carries a value, classifies loss_cause as Unclassified, or simply has none for that field
   (e.g. a coverage reclassified outside the loss-cause whitelist). Use this for both the
   feed-at-edit and current-feed slots; the corrected-value slot keeps formatCorrectionValue's
   "(cleared)" wording because that one really can be a deliberate human blank. */
export const formatFeedValue = (field, value) =>
  value == null || value === '' ? (field === 'loss_cause' ? 'Unclassified' : '(none)') : formatCorrectionValue(field, value);
